<script lang="ts">
    import { get } from "svelte/store";
    import { onDestroy, onMount } from "svelte";
    import type { Unsubscriber } from "svelte/store";
    import type { WorkAdventureComponent } from "../../../types/component";
    import type { MenuItem } from "../../Stores/MenuStore";
    import {
        activeSubMenuStore,
        customMenuIframe,
        menuVisiblilityStore,
        SubMenusInterface,
        subMenusStore,
    } from "../../Stores/MenuStore";
    import { menuInputFocusStore } from "../../Stores/MenuInputFocusStore";
    import { sendMenuClickedEvent } from "../../Api/Iframe/Ui/MenuItem";
    import { LL } from "../../../i18n/i18n-svelte";
    import { analyticsClient } from "../../Administration/AnalyticsClient";
    import ButtonClose from "../Input/ButtonClose.svelte";
    import SettingsSubMenu from "./SettingsSubMenu.svelte";
    import ProfileSubMenu from "./ProfileSubMenu.svelte";
    import AboutRoomSubMenu from "./AboutRoomSubMenu.svelte";
    import ContactSubMenu from "./ContactSubMenu.svelte";
    import CustomSubMenu from "./CustomSubMenu.svelte";
    import ReportSubMenu from "./ReportSubMenu.svelte";
    import ChatSubMenu from "./ChatSubMenu.svelte";
    import ShortcutSubMenu from "./ShortcutSubMenu.svelte";
    import HelpSubMenu from "./HelpSubMenu.svelte";

    function getActiveMenu(index: number): MenuItem {
        return $subMenusStore[index] ?? subMenusStore.findByKey(SubMenusInterface.settings);
    }

    let activeSubMenu: MenuItem = $state(getActiveMenu($activeSubMenuStore));
    let activeComponent: WorkAdventureComponent = $state(ProfileSubMenu);
    let props: { url: string; allowApi: boolean; allow?: string } = $state({
        url: "",
        allowApi: false,
        allow: undefined,
    });
    let unsubscriberSubMenuStore: Unsubscriber;
    let unsubscriberActiveSubMenuStore: Unsubscriber;
    let dialogElement: HTMLDivElement;
    let previouslyFocusedElement: HTMLElement | null = null;

    onMount(async () => {
        previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        unsubscriberActiveSubMenuStore = activeSubMenuStore.subscribe((value) => {
            switchMenu(getActiveMenu(value)).catch((e) =>
                console.error("Failed to switch menu on activeSubMenuStore change", e),
            );
        });
        unsubscriberSubMenuStore = subMenusStore.subscribe(() => {
            if (!$subMenusStore.includes(activeSubMenu)) {
                switchMenu(getActiveMenu($activeSubMenuStore)).catch((e) =>
                    console.error("Failed to switch menu on subMenusStore change", e),
                );
            }
        });

        await switchMenu(getActiveMenu($activeSubMenuStore));
        dialogElement.querySelector<HTMLElement>("button")?.focus();
    });

    onDestroy(() => {
        menuInputFocusStore.set(false);
        if (unsubscriberSubMenuStore) {
            unsubscriberSubMenuStore();
        }
        if (unsubscriberActiveSubMenuStore) {
            unsubscriberActiveSubMenuStore();
        }
        previouslyFocusedElement?.focus();
    });

    async function switchMenu(menu: MenuItem) {
        if (menu.type === "translated") {
            activeSubMenu = menu;
            activeSubMenuStore.activateByMenuItem(menu);
            switch (menu.key) {
                case SubMenusInterface.profile:
                    activeComponent = ProfileSubMenu;
                    analyticsClient.menuProfile();
                    break;
                case SubMenusInterface.settings:
                    activeComponent = SettingsSubMenu;
                    analyticsClient.menuSetting();
                    break;
                case SubMenusInterface.aboutRoom:
                    activeComponent = AboutRoomSubMenu;
                    analyticsClient.menuCredit();
                    break;
                case SubMenusInterface.contact:
                    activeComponent = ContactSubMenu;
                    analyticsClient.menuContact();
                    break;
                case SubMenusInterface.globalMessages:
                    activeComponent = (await import("./GlobalMessagesSubMenu.svelte")).default;
                    analyticsClient.globalMessage();
                    break;
                case SubMenusInterface.report:
                    activeComponent = ReportSubMenu;
                    analyticsClient.reportIssue();
                    break;
                case SubMenusInterface.chat:
                    activeComponent = ChatSubMenu;
                    analyticsClient.menuChat();
                    break;
                case SubMenusInterface.shortcuts:
                    activeComponent = ShortcutSubMenu;
                    analyticsClient.menuShortcuts();
                    break;
                case SubMenusInterface.help:
                    activeComponent = HelpSubMenu;
                    break;
            }
        } else {
            // Save custom menu click for analytics
            analyticsClient.menuCustom(menu.key);

            const customMenu = customMenuIframe.get(menu.key);
            if (customMenu !== undefined) {
                activeSubMenu = menu;
                props = { url: customMenu.url, allowApi: customMenu.allowApi, allow: customMenu.allow };
                activeComponent = CustomSubMenu;
            } else {
                sendMenuClickedEvent(menu.key);
                menuVisiblilityStore.set(false);
            }
        }
    }

    function closeMenu() {
        activeSubMenuStore.activateByIndex(0);
        menuVisiblilityStore.set(false);
    }

    function onKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            closeMenu();
            return;
        }
        if (e.key !== "Tab") {
            return;
        }

        const focusableElements = Array.from(
            dialogElement.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])',
            ),
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements.at(-1);
        if (!firstElement || !lastElement) {
            e.preventDefault();
        } else if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }

    let subMenuTranslations = $derived(
        $subMenusStore.map((subMenu) => (subMenu.type === "scripting" ? subMenu.label : $LL.menu.sub[subMenu.key]())),
    );
    let activeSubMenuTitle = $derived(
        activeSubMenu.type === "scripting" ? activeSubMenu.label : $LL.menu.sub[activeSubMenu.key](),
    );

    const SvelteComponent = $derived(activeComponent);
</script>

<svelte:window onkeydown={onKeyDown} />

<div
    bind:this={dialogElement}
    data-testid="nutrivera-preferences"
    role="dialog"
    aria-modal="true"
    aria-labelledby="nutrivera-preferences-title"
    class="close-window pointer-events-auto absolute inset-0 z-[900] m-auto flex h-[min(600px,calc(100dvh-32px))] w-[min(800px,calc(100dvw-32px))] max-w-[800px] overflow-clip rounded-[14px] border border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-bg)] font-sans text-[var(--nutrivera-shell-text)] shadow-[0_18px_50px_rgb(0_0_0/45%)]"
>
    <nav
        aria-label={$LL.menu.title()}
        class="menu-nav-sidebar w-14 flex-none overflow-y-auto border-e border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-raised)] p-2 sm:w-[216px] sm:p-3"
    >
        {#each $subMenusStore as submenu, i (`${submenu.key}_${submenu.type}`)}
            {@const visibleStore = submenu.visible}
            {#if get(visibleStore)}
                <button
                    type="button"
                    aria-label={subMenuTranslations[i]}
                    aria-current={activeSubMenu === submenu ? "page" : undefined}
                    class="menu-item mb-1 flex h-10 w-full items-center justify-center rounded-[10px] px-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--nutrivera-shell-accent)] sm:justify-start sm:px-3 {activeSubMenu ===
                    submenu
                        ? 'bg-[var(--nutrivera-shell-hover)] text-[var(--nutrivera-shell-text)]'
                        : 'text-[var(--nutrivera-shell-muted)] hover:bg-[var(--nutrivera-shell-hover)] hover:text-[var(--nutrivera-shell-text)]'}"
                    onclick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        switchMenu(submenu).catch((error) => {
                            console.error("Failed to switch menu", error);
                        });
                    }}
                >
                    <span class="sm:hidden" aria-hidden="true">{subMenuTranslations[i].charAt(0)}</span>
                    <span class="sr-only sm:not-sr-only">{subMenuTranslations[i]}</span>
                </button>
            {/if}
        {/each}
    </nav>

    <section class="flex min-h-0 min-w-0 flex-1 flex-col">
        <header
            class="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-solid border-[var(--nutrivera-shell-border)] bg-[var(--nutrivera-shell-bg)] px-4 sm:px-6"
        >
            <h1 id="nutrivera-preferences-title" class="truncate text-lg font-semibold">{activeSubMenuTitle}</h1>
            <ButtonClose onclick={closeMenu} id="closeMenu" dataTestId="closeMenuBtn" size="md" />
        </header>
        <div id="submenu" class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
            <SvelteComponent {...props} />
        </div>
    </section>
</div>

<style>
    .menu-nav-sidebar::-webkit-scrollbar {
        display: none;
    }
</style>
